import React from 'react';
import BrandDropdown from 'shared/components/ui/BrandDropdown';
import SegmentedTabs from 'shared/components/ui/SegmentedTabs';
import SelfImprovingPanel from './SelfImprovingPanel';
import { Section, inlineRow, numInput } from './formParts';

/**
 * The eval-level knobs, set when an eval is created and editable after —
 * every change is part of the next frozen version (TRO-460):
 *
 *   self-improving    not a version setting: an eval alert + an agent that has
 *                     Ask propose a change when it fires (SelfImprovingPanel)
 *   backfill policy   Off, or when the production version changes re-score
 *                     history by time (the last N hours/days) or by volume
 *                     (the last N units).
 *
 * Judge steps pick their own provider and model inside the step; there is
 * no eval-level default. Shared by the create page and the editor's settings
 * panel so the two never describe the same decision in different words.
 */

export const UNIT_WORD = { run: 'runs', span: 'spans', conversation: 'conversations' };

export const DEFAULT_CADENCE = Object.freeze({ kind: 'volume', every: 100 });
const UNIT_OPTIONS = [{ value: 'hours', label: 'hours' }, { value: 'days', label: 'days' }];

export const DEFAULT_SETTINGS = Object.freeze({
  self_improving: false,
  improvement_model: null,
  improvement_cadence: null,
  backfill_policy: { mode: 'off' },
});

const positive = (raw, fallback) => Math.max(1, Math.floor(Number(raw) || fallback || 1));

export default function EvalSettingsFields({ target = 'run', value, onChange, integrations = [], modelsFor = () => [], readOnly = false, teamId = null, lineageId = null }) {
  const v = { ...DEFAULT_SETTINGS, ...(value || {}) };
  const unit = UNIT_WORD[target] || 'events';
  const bp = v.backfill_policy || { mode: 'off' };
  const setBackfill = (next) => onChange({ backfill_policy: next });
  const backfillKind = bp.mode === 'off' ? 'off' : bp.mode === 'last_n' ? 'volume' : 'time';

  return (
    <div>
      {lineageId && (
        <Section title="Self-improving">
          {/* An alert on the eval + an agent that has Ask improve it when the alert fires. */}
          <SelfImprovingPanel teamId={teamId} lineageId={lineageId} readOnly={readOnly} />
        </Section>
      )}

      <Section title="Re-score history when production changes">
        <div className={inlineRow}>
          <SegmentedTabs ariaLabel="Backfill policy" value={backfillKind}
            onChange={(kind) => setBackfill(kind === 'off' ? { mode: 'off' }
              : kind === 'time' ? (backfillKind === 'time' ? bp : { mode: 'last_days', n: 7 })
                : { mode: 'last_n', n: bp.mode === 'last_n' ? bp.n : 500 })}
            options={[{ value: 'off', label: 'Off' }, { value: 'time', label: 'Time' }, { value: 'volume', label: 'Volume' }]} />
          {bp.mode !== 'off' && (
            <>
              <span>the last</span>
              <input type="number" min={1} step={1} value={bp.n ?? ''} readOnly={readOnly} aria-label="Backfill amount"
                onChange={(e) => setBackfill({ ...bp, n: positive(e.target.value, 1) })} className={numInput} />
              {bp.mode === 'last_n' ? <span>{unit}</span> : (
                <>
                  <div className="w-28"><BrandDropdown ariaLabel="Backfill unit" value={bp.mode === 'last_hours' ? 'hours' : 'days'} disabled={readOnly} onChange={(u) => setBackfill({ ...bp, mode: u === 'hours' ? 'last_hours' : 'last_days' })} options={UNIT_OPTIONS} /></div>
                  <span>of {unit}</span>
                </>
              )}
            </>
          )}
        </div>
      </Section>
      {!lineageId && (
        <Section title="Self-improving" className="mb-0">
          <span className="text-[12px] text-[color:var(--text-muted)]">Set up after creating.</span>
        </Section>
      )}
    </div>
  );
}
